/**
 * FAL Disclaimer Page
 *
 * Interstitial page shown to individual users before property request form.
 * Explains that properties will be listed under Safaqat 24's FAL licence with
 * 2.5% commission. Acceptance persisted in localStorage.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const FAL_ACCEPTANCE_KEY = 'safaqat24.fal.accepted';

export default function FALDisclaimerPage() {
  const { t, i18n } = useTranslation('registration');
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();

  const [accepted, setAccepted] = useState(false);

  // Check if user already accepted - auto-forward if so
  useEffect(() => {
    const hasAccepted = localStorage.getItem(FAL_ACCEPTANCE_KEY);
    if (hasAccepted === 'true') {
      navigate('/individual-property-request', { replace: true });
    }
  }, [navigate]);

  const handleContinue = () => {
    if (!accepted) return;

    // Store acceptance in localStorage
    localStorage.setItem(FAL_ACCEPTANCE_KEY, 'true');

    // Navigate to property request form
    navigate('/individual-property-request');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
      <Navigation />

      <div className="pt-20 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Content Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 md:p-10" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#004D40] flex items-center justify-center">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-3">
                {t('fal.title')}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {t('fal.subtitle')}
              </p>
            </div>

            {/* Body Content */}
            <div className="space-y-6 mb-8">
              <div className="p-5 bg-[var(--pv-color-surface-sunken)] dark:to-blue-900/20 rounded-xl border border-[#1B7566]/30 dark:border-[#1B7566]/20">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  {t('fal.body1')}
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t('fal.body2')}
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
                  {t('fal.body3')}
                </p>
              </div>

              {/* How It Works Section */}
              <div>
                <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] dark:text-white mb-4 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-[#004D40]"></i>
                  {t('fal.howItWorks')}
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 text-[#004D40] font-semibold">
                      1
                    </div>
                    <div className="flex-1 pt-1">
                      <p className="text-gray-700 dark:text-gray-300">
                        {t('fal.step1')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 text-[#004D40] font-semibold">
                      2
                    </div>
                    <div className="flex-1 pt-1">
                      <p className="text-gray-700 dark:text-gray-300">
                        {t('fal.step2')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 text-[#004D40] font-semibold">
                      3
                    </div>
                    <div className="flex-1 pt-1">
                      <p className="text-gray-700 dark:text-gray-300">
                        {t('fal.step3')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 text-[#004D40] font-semibold">
                      4
                    </div>
                    <div className="flex-1 pt-1">
                      <p className="text-gray-700 dark:text-gray-300">
                        {t('fal.step4')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Commission Notice */}
                <div className="mt-4 p-4 bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/10 rounded-lg border border-[#004D40]/20">
                  <p className="text-sm text-[#004D40] dark:text-[#1B7566] font-medium flex items-center gap-2">
                    <i className="ri-information-line"></i>
                    {t('individual.commission', { rate: 2.5 })}
                  </p>
                </div>
              </div>
            </div>

            {/* Acceptance Checkbox */}
            <div className="mb-6">
              <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-[#004D40] dark:hover:border-[#004D40] transition-colors">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-[#004D40] focus:ring-[#004D40] focus:ring-offset-0 cursor-pointer"
                />
                <span className="flex-1 text-gray-700 dark:text-gray-300 select-none">
                  {t('fal.checkbox')}
                </span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={handleGoBack}
                className="w-full sm:w-auto"
              >
                <i className={`ri-arrow-left-line ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`}></i>
                {t('fal.back')}
              </Button>
              <Button
                onClick={handleContinue}
                disabled={!accepted}
                className="w-full sm:flex-1"
              >
                {t('fal.continue')}
                <i className={`ri-arrow-right-line ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`}></i>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
